import { useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ApiError } from '@/lib/api'
import { QK, invalidateFor } from '@/lib/query-keys'
import { DsToast } from '@/ds'
import { useSessionStore } from '@/stores/session.store'
import { useCountries } from '@/features/geo/geo.api'
import { viewCountryApi } from '@/features/view-country/view-country.api'
import { viewCountryStrings as s } from '@/features/view-country/strings'
import type { Country } from '@/types/models'

export const viewCountryKeys = {
  countries: [...QK.profile, 'countries'] as const,
}

/** The value of the list filter meaning "every country": the API is then called without `country`. */
export const ALL_COUNTRIES = 'all'

export interface CountryOption {
  iso2: string
  name: string
  emoji?: string
  currency?: string
}

/** Name, flag and currency of an ISO2 code from the geo list; the bare code while that list is loading or lacks it. */
export function countryOption(iso2: string, countries: Country[] | undefined): CountryOption {
  const code = iso2.trim().toUpperCase()
  const c = countries?.find((x) => x.iso2.toUpperCase() === code)
  return { iso2: code, name: c?.name || code, emoji: c?.emoji, currency: c?.currency || undefined }
}

export const countryLabel = (o: CountryOption) => (o.emoji ? `${o.emoji} ${o.name}` : o.name)

// The country whose figures the dashboards show (LP-491). A viewing preference only: never feed it to capabilities,
// which follow the user's own address country. undefined only against a backend that predates it.
export function useViewCountry(): string | undefined {
  return useSessionStore((st) => st.user?.viewCountry?.trim().toUpperCase() || undefined)
}

/** One code's display data; undefined for no code. */
export function useCountryInfo(iso2: string | undefined): CountryOption | undefined {
  const geo = useCountries()
  return useMemo(() => (iso2 ? countryOption(iso2, geo.data) : undefined), [iso2, geo.data])
}

/** What the switch offers (GET /profile/countries), always including the current view. */
export function useCountryOptions() {
  const view = useViewCountry()
  const signedIn = useSessionStore((st) => !!st.user)
  const query = useQuery({ queryKey: viewCountryKeys.countries, queryFn: viewCountryApi.countries, enabled: signedIn, staleTime: 5 * 60 * 1000 })
  const geo = useCountries()
  const options = useMemo(() => {
    const codes = [...(query.data ?? [])]
    if (view && !codes.includes(view)) codes.unshift(view)
    return codes.map((c) => countryOption(c, geo.data))
  }, [query.data, view, geo.data])
  return { options, loading: query.isPending }
}

// Saving the viewing country moves the session user at once (so every key built on it changes), then refetches
// every combined figure and list (`invalidateFor('viewCountry')`). Plain async so a toast action can call it after
// the page that offered it has gone.
export async function switchViewCountry(iso2: string): Promise<string> {
  const code = iso2.trim().toUpperCase()
  await viewCountryApi.save(code)
  const { user, setUser } = useSessionStore.getState()
  if (user) setUser({ ...user, viewCountry: code })
  await invalidateFor('viewCountry')
  return code
}

export const viewCountryErrorMessage = (e: unknown) => (e instanceof ApiError ? e.message : s.somethingWentWrong)

export function useSetViewCountry() {
  return useMutation({ mutationFn: switchViewCountry, onError: (e) => DsToast.error(viewCountryErrorMessage(e)) })
}

// A list's country filter lives in `?country=` and defaults to the viewing country: `country=all` asks for every
// country (no `country` sent) and hides money totals, since they would add two currencies. Choosing the viewing
// country clears the param, so the list keeps following it.
export function useListCountry() {
  const [params, setParams] = useSearchParams()
  const view = useViewCountry()
  const raw = params.get('country')?.trim() ?? ''
  const isAll = raw.toLowerCase() === ALL_COUNTRIES
  const country = isAll ? undefined : raw.toUpperCase() || view
  const set = (value: string) => {
    const next = new URLSearchParams(params)
    const v = value.trim()
    if (!v || v.toUpperCase() === view) next.delete('country')
    else next.set('country', v.toLowerCase() === ALL_COUNTRIES ? ALL_COUNTRIES : v.toUpperCase())
    setParams(next, { replace: true })
  }
  return {
    /** ISO2 sent to the API; undefined = every country. */
    country,
    isAll,
    /** The control's value: an ISO2 or ALL_COUNTRIES. */
    value: isAll ? ALL_COUNTRIES : country ?? ALL_COUNTRIES,
    set,
    // Totals are single-currency only when the list is one country. Without any viewing country (an older backend)
    // nothing changes from before.
    showTotals: !isAll,
  }
}

/** A new record's country when it is not the viewing country (it will be listed under its own); undefined otherwise. */
export function useOtherCountry(iso2: string | undefined): CountryOption | undefined {
  const view = useViewCountry()
  const code = iso2?.trim().toUpperCase() || undefined
  const info = useCountryInfo(code)
  return code && view && code !== view ? info : undefined
}

export type ListCountryFilter = ReturnType<typeof useListCountry>
